// Базовые типы, общие для всех контрактов модулей.
import { z } from "zod";

/** UUID v7 — приложение генерирует. Валидация мягкая (строка-UUID). */
export const uuidSchema = z.string().uuid();

/** ISO 8601 datetime (UTC, например `2026-05-12T14:30:00Z`). */
export const isoDateTimeSchema = z.string().datetime({ offset: true });

/** ISO 8601 date (без времени, например `2026-05-12`). */
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "invalid ISO date");

/** ISO 639-1 locale (`ru`, `en`, `kk`, ...). */
export const localeSchema = z
	.string()
	.regex(/^[a-z]{2}(-[A-Z]{2})?$/, "invalid ISO 639-1 locale")
	.describe("ISO 639-1 (опц. с регионом)");

/**
 * Sentinel-значение «язык генерируемых материалов = язык исходного материала».
 * Хранится в `users.answer_locale` наряду с ISO-кодами и `NULL`.
 */
export const GENERATION_LOCALE_SOURCE = "source" as const;

/**
 * Предпочтение языка генерации AI-материалов (`users.answer_locale`). Три режима:
 *   - `null`     → язык интерфейса пользователя (`users.language`) — дефолт;
 *   - `'source'` → язык исходного материала (`material.language`);
 *   - `'<ISO>'`  → конкретный фиксированный язык (`ru`, `en`, ...).
 * Резолв в конкретный ISO — на сервере (`resolveGenerationLocale`).
 */
export const generationLocalePrefSchema = z.union([
	localeSchema,
	z.literal(GENERATION_LOCALE_SOURCE),
]);

/**
 * Общий ISO 3166-1 alpha-2 код страны (`RU`, `US`, `KZ`, `ES`, ...).
 * Используется для billing country, profile country, и любых "any country" полей.
 * Принимает любые две заглавные ASCII-буквы. ВАЛИДАЦИЯ конкретного списка стран —
 * на уровне сервера через `Intl.supportedValuesOf('region')`.
 */
export const countryCodeSchema = z
	.string()
	.length(2)
	.regex(/^[A-Z]{2}$/, "Код страны должен быть ISO 3166-1 alpha-2");

/**
 * Образовательные системы, поддерживаемые регионализованными Study Plan templates
 * и exam-specific presets. **Открытая TEXT-схема:** ISO 3166-1 alpha-2 (две заглавные
 * буквы) ИЛИ литерал 'OTHER'. Конкретный набор стран валидируется server-side
 * через `EDUCATION_COUNTRY_CODES_SUPPORTED` — добавление новой страны не требует
 * редеплоя клиента (CLAUDE.md §10 — i18n / rules/schema-first.md §2.3).
 * НЕ использовать для billing/profile — там `countryCodeSchema`.
 */
export const EDUCATION_COUNTRY_CODES_SUPPORTED = [
	"RU",
	"US",
	"UK",
	"KZ",
	"DE",
	"FR",
	"CN",
	"IN",
	"OTHER",
] as const;

/** @deprecated Используй `EDUCATION_COUNTRY_CODES_SUPPORTED`. */
export const EDUCATION_COUNTRY_CODES = EDUCATION_COUNTRY_CODES_SUPPORTED;

export const educationCountryCodeSchema = z
	.string()
	.min(2)
	.max(8)
	.regex(/^([A-Z]{2}|OTHER)$/, "invalid education country");
export type EducationCountryCode = z.infer<typeof educationCountryCodeSchema>;

/** @deprecated Используй `EDUCATION_COUNTRY_CODES_SUPPORTED`. */
export const COUNTRY_CODES = EDUCATION_COUNTRY_CODES_SUPPORTED;
/** @deprecated Используй `EducationCountryCode`. */
export type CountryCode = EducationCountryCode;

/** IANA timezone (`Europe/Moscow`, `America/New_York`). */
export const timezoneSchema = z
	.string()
	.min(1)
	.regex(/^[A-Za-z]+(?:\/[A-Za-z_+-]+){0,3}$/, "invalid IANA timezone");

/**
 * Единый статус жизненного цикла AI-генерируемого артефакта (notes / tests /
 * flashcard-set и т.п.):
 *   - `generating` — placeholder создан, Inngest-workflow ещё идёт;
 *   - `ready`      — артефакт успешно сгенерирован (persist завершён);
 *   - `failed`     — исчерпаны retries (Inngest onFailure), есть error message.
 *
 * Зеркало колонки `*.generation_status` (TEXT, DEFAULT 'ready'). НЕ enum в БД —
 * расширяемо без миграции. Manual-артефакты создаются сразу 'ready'.
 *
 * Прим.: flashcards используют отдельный legacy `processing_status` с лишним
 * 'idle' — его миграция к этому типу вне scope (enum-миграция отдельно).
 */
export const artifactGenerationStatusSchema = z.enum(["generating", "ready", "failed"]);
export type ArtifactGenerationStatus = z.infer<typeof artifactGenerationStatusSchema>;

/** AI provenance — обязательно у каждого AI-сгенерированного артефакта. */
export const aiProvenanceSchema = z.object({
	aiModel: z.string().min(1),
	promptVersion: z.string().min(1),
	generationTraceId: z.string().min(1),
	// Code-review N4: OpenAI запрещён CLAUDE.md «Что НЕ используем» — только Gemini.
	provider: z.literal("gemini").optional(),
	generatedAt: isoDateTimeSchema,
});
export type AiProvenance = z.infer<typeof aiProvenanceSchema>;

/**
 * Адрес учётной записи, каким он ПРИШЁЛ из единого профиля, — не обязательно
 * почтовый адрес.
 *
 * IEC ID отдаёт в поле `email` идентификатор входа, и у части учёток это логин
 * без `@` (реальный случай — `iec_riga_3`). Better Auth кладёт его в
 * `users.email` как есть: выдумать адрес вместо экосистемы мы не можем, а
 * колонка обязательная и уникальная.
 *
 * Поэтому в ЛЮБОЙ схеме, которая лишь ЗЕРКАЛИТ эту колонку наружу, стоит
 * `z.string()`, а не `.email()`: строгая output-валидация роняет весь ответ
 * из-за одной такой строки. Цена ошибки — не косметика: `auth.me` зовётся при
 * каждой загрузке приложения, и его 500 отрезает человека от сервиса целиком
 * (Hawk 6a9037a7f711d01de6aba03e, 27.08.2026; тот же класс раньше уронил
 * `admin.users.list` — OXEM-21).
 *
 * `.email()` остаётся там, где адрес ВВОДИТ человек (форма входа, dev-login):
 * там формат — часть требования к вводу, а не наблюдение за чужими данными.
 */
export const accountEmailSchema = z
	.string()
	.min(1)
	.describe("идентификатор входа из единого профиля (не обязательно email)");
